import * as React from 'react';
import { styled } from '@glitz/react';
import {
  createErrorBoundary,
  setComponentState,
  translate,
  useDynamicData,
  useMatchSmallViewport,
} from '@avensia/excite';
import { bareListDecorator } from 'webapp/decorators/list';
import {
  createMaxSmallViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import BaseLink from 'webapp/components/Link';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { BrandAndDesignerContent } from './BrandAndDesignerContent';
import { Theme } from 'webapp/theme';
import { useVariantSelector } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';
import { ExpandableBox } from './expandable-box';
import { maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import ProductListCarousel from './ProductListCarousel';
import { useCustomerType } from '@avensia/excite-commercetools';
import { ButtonLink } from 'webapp/components/button';
import { addSelectContentEventToDataLayer } from 'webapp/tracking/events/content';
import { secondaryGrey } from 'webapp/shared/sharedTheme';
import { ProductPageContent } from './ProductPageContent';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { useFormatPrice } from '@avensia/excite-ui';

type PropType = NordicNest.Api.ProductPageApiModel & {
  accessories: NordicNest.Api.PartialProductApiModel[] | undefined;
  spareParts: NordicNest.Api.PartialProductApiModel[] | undefined;
  openAccordion?: string;
  setOpenAccordion: (id: undefined | string) => void;
  specificationsContainerRef: React.RefObject<HTMLDivElement | null>;
};

export default React.memo(
  styled(
    React.forwardRef(function ProductDetails(props: PropType, ref: React.Ref<HTMLDivElement>) {
      React.useEffect(() => {
        setComponentState({ id: props.sku + '-accordion', state: props.openAccordion });
      }, [props.openAccordion, props.sku]);

      const [designersInfo] = useDynamicData<NordicNest.Api.DesignersInfoApiModel>({
        context: props.designerInfoContexts,
      });

      const [brandInfo] = useDynamicData<NordicNest.Api.BrandInfoApiModel>({
        context: props.brandInfoContext,
      });

      const [wellKnownUrls] = useDynamicData<NordicNest.Api.WellknownPagesApiModel>();
      const isSmallViewport = useMatchSmallViewport();
      const formatPrice = useFormatPrice();
      const { isCompanyCustomer } = useCustomerType();

      const hasDesignerInfo = Boolean(designersInfo?.designers && designersInfo.designers.length > 0);

      const sendOpenAccordionEvent = (description: string, id?: string) => {
        props.setOpenAccordion(id);
        addSelectContentEventToDataLayer({
          id: '',
          link: '',
          type: 'accordion',
          action: id ? 'open' : 'close',
          content: description,
        });
      };

      return (
        <Container ref={ref} key={props.productId} data-scroll-id>
          {props.description && (
            <ExpandableBox
              id={`${props.sku}-product-description`}
              header={translate(t => t.productPage.detailsDescriptionHeading)}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('product_description', id)}
            >
              <ProductDescription>
                <Document styleDecorators={styles} elements={props.description} />
                <ProductPageContent pdpContentContext={props.pdpContentContext} />
              </ProductDescription>
            </ExpandableBox>
          )}
          <div ref={props.specificationsContainerRef}>
            <ExpandableBox
              id={`${props.sku}-product-specification`}
              header={translate(t => t.productPage.productSpecification)}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('product_specification', id)}
            >
              <SpecificationInfoList {...props} />
            </ExpandableBox>
          </div>
          {(Boolean(brandInfo?.description && brandInfo?.description?.length > 0) || hasDesignerInfo) && (
            <ExpandableBox
              id={`${props.sku}-brand-and-designer`}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('brand_and_designer', id)}
              header={
                designersInfo?.designers && designersInfo?.designers.length > 0
                  ? translate(t => t.productPage.detailsAboutBrandAndDesignerHeading, { brand: props.brand })
                  : translate(t => t.productPage.detailsAboutBrandHeading, { brand: props.brand })
              }
            >
              <BrandAndDesignerContent brandInfo={brandInfo} {...props} designers={designersInfo?.designers} />
            </ExpandableBox>
          )}

          {props.showStringConfiguration && !isSmallViewport && (
            <ExpandableBox
              id={`${props.sku}-configuration`}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('configuration', id)}
              header={translate(t => t.productPage.stringProductConfigurationHeading)}
            >
              <p>{translate(t => t.productPage.stringProductConfigurationDescription)}</p>
              <ProductConfigurationButton
                as={Theme.Monochrome}
                to={
                  wellKnownUrls?.stringProductConfigurationPageUrl
                    ? `${wellKnownUrls?.stringProductConfigurationPageUrl}${
                        props.bundleExternalId ? '?designtemplate=' + props.bundleExternalId : ''
                      }`
                    : ''
                }
                {...NnTestIdProps.StringConfiguratorPdpButton}
              >
                {translate(t => t.productPage.stringProductConfigurationHeading)}
              </ProductConfigurationButton>
            </ExpandableBox>
          )}
          {props.spareParts && props.spareParts.length > 0 && (
            <ExpandableBox
              id={`${props.sku}-spareParts`}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('spareParts', id)}
              header={translate(t => t.productPage.spareParts)}
              contentPadding={false}
            >
              <StyledProductList id={'pdp_spareParts_' + props.sku} name="pdp_spareParts" products={props.spareParts} />
            </ExpandableBox>
          )}
          {props.accessories && props.accessories.length > 0 && (
            <ExpandableBox
              id={`${props.sku}-accessories`}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('accessories', id)}
              header={translate(t => t.productPage.accessoriesHeading)}
              contentPadding={false}
            >
              <StyledProductList
                id={'pdp_accessories_' + props.sku}
                name="pdp_accessories"
                products={props.accessories}
              />
            </ExpandableBox>
          )}
          {props.price.showPriceHistory && props.price.lowestHistoricalPrice && (
            <ExpandableBox
              id={`${props.sku}-price-history`}
              openAccordion={props.openAccordion}
              setOpenAccordion={id => sendOpenAccordionEvent('price_history', id)}
              header={translate(t => t.productPage.detailsPriceHistoryHeading)}
            >
              <PriceHistoryContent suppressHydrationWarning>
                {translate(t => t.productPage.detailsPriceHistoryDescription, {
                  price: isCompanyCustomer
                    ? formatPrice(props.price.lowestHistoricalPrice.exclVat)
                    : formatPrice(props.price.lowestHistoricalPrice.inclVat),
                })}
              </PriceHistoryContent>
            </ExpandableBox>
          )}
        </Container>
      );
    }),
  ),
);

const Container = styled.div(
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.xxl },
    margin: { top: 128, bottom: 90 },
  }),
  {
    margin: { top: 65, bottom: 30 },
  },
);

const ProductConfigurationButton = styled(ButtonLink, {
  width: '100%',
  maxWidth: 300,
});

const SpecificationInfoList = createErrorBoundary(function InfoList(props: PropType) {
  const { selectors } = useVariantSelector();

  const context: NordicNest.Api.ProductPageBundleFactsContext = {
    selectedProducts: props.isBundle ? props.bundlePartSkus : selectors.filter(v => v.selectedVariant).map(v => v.selectedVariant!.articleNumber),
  };

  const [bundleFacts] = useDynamicData<NordicNest.Api.ProductPageBundleFactsApiModel>({
    context: context,
    shouldResolveData: context.selectedProducts?.length > 0 && (props.isPackage || props.isBundle),
  });

  const sendLinkEvent = (url: string) => {
    addSelectContentEventToDataLayer({
      id: '',
      link: url,
      type: 'link',
      content: '',
      action: '',
    });
  };

  return (
    <Specifications>
      {props.isPreDefinedBundle && (
        <PartsWrapper>
          <SubTitle>{translate(t => t.productPage.bundlePartsHeading)}</SubTitle>
          <Parts>
            {props.packageParts?.map(p => (
              <PartItem key={p.key}>
                <Quantity>{p.quantity} x </Quantity>
                {p.url ? (
                  <Link to={p.url} onClick={() => sendLinkEvent(p.url)}>
                    {p.displayName}
                  </Link>
                ) : (
                  <div>{p.displayName}</div>
                )}
                {props.packageParts !== undefined && props.packageParts.length === 1 && false ? <Sku>{p.key}</Sku> : <></>}
              </PartItem>
            ))}
          </Parts>
          <BundleInfoList {...props} />
        </PartsWrapper>
      )}
      <ProductInfoList {...props} />
      {(props?.isPackage||props?.isBundle) &&
        bundleFacts?.selectedProducts.map((p, i) => (
          <PackagePartFactSection key={i + 'packagefact'}>
            <SubSubTitle>{p.displayName}</SubSubTitle>
            <ProductInfoList {...p} isBundle />
          </PackagePartFactSection>
        ))}
    </Specifications>
  );
});

const BundleInfoList = createErrorBoundary(function InfoList(props: PropType) {
  const sendLinkEvent = (url: string) => {
    addSelectContentEventToDataLayer({
      id: '',
      link: url,
      type: 'link',
      content: '',
      action: '',
    });
  };

  return (
    <>
      {/*<StyledSubTitle>{group.label}</StyledSubTitle>*/}
      <List>
        {props.brand && (
          <Item>
            <Key>{translate(t => t.productPage.generalInformationBrand)}</Key>
            <Value>
              <Link to={props.brandUrl} onClick={() => sendLinkEvent(props.brandUrl)}>
                {props.brand}
              </Link>
            </Value>
          </Item>
        )}
        {props.designerLinks && props.designerLinks.length > 0 && (
          <Item>
            <Key>{translate(t => t.productPage.generalInformationDesigner)}</Key>
            <Value>
              {props.designerLinks.map((designer, i) => (
                <Link key={i} to={designer.to} onClick={() => sendLinkEvent(designer.to)}>
                  {designer.title}
                </Link>
              ))}
            </Value>
          </Item>
        )}
        {props.collection && (
          <Item>
            <Key>{translate(t => t.productPage.generalInformationCollection)}</Key>
            <Value>
              <Link to={props.collectionUrl} onClick={() => sendLinkEvent(props.collectionUrl)}>
                {props.collection}
              </Link>
            </Value>
          </Item>
        )}
        {props.sku && (
          <Item>
            <Key>{translate(t => t.productPage.articleNumber)}</Key>
            <Value>{props.displaySku ?? props.sku}</Value>
          </Item>
        )}
      </List>
      <ProductInfoList {...props} />
    </>
  );
});

const ProductInfoList = createErrorBoundary(function InfoList({
  productInfoGroups,
  brand,
  brandUrl,
  collection,
  collectionUrl,
  designerLinks,
  sku,
  displaySku,
  isBundle,
}: NordicNest.Api.ProductPageApiModel & { isBundle?: boolean }) {
  const sendLinkEvent = (url: string) => {
    addSelectContentEventToDataLayer({
      id: '',
      link: url,
      type: 'link',
      content: '',
      action: '',
    });
  };
  return (
    <>
      {productInfoGroups?.map((group, i) => (
        <ListWrapper key={`${group.label}${isBundle ? '-bundle' : ''}`}>
          <StyledSubTitle>{group.label}</StyledSubTitle>
          <List>
            {i === 0 && (
              <>
                {brand && (
                  <Item>
                    <Key>{translate(t => t.productPage.generalInformationBrand)}</Key>
                    <Value>
                      <Link to={brandUrl} onClick={() => sendLinkEvent(brandUrl)}>
                        {brand}
                      </Link>
                    </Value>
                  </Item>
                )}
                {designerLinks && designerLinks.length > 0 && (
                  <Item>
                    <Key>{translate(t => t.productPage.generalInformationDesigner)}</Key>
                    <Value>
                      {designerLinks.map((designer, i) => (
                        <Link key={i} to={designer.to} onClick={() => sendLinkEvent(designer.to)}>
                          {designer.title}
                        </Link>
                      ))}
                    </Value>
                  </Item>
                )}
                {collection && (
                  <Item>
                    <Key>{translate(t => t.productPage.generalInformationCollection)}</Key>
                    <Value>
                      <Link to={collectionUrl} onClick={() => sendLinkEvent(collectionUrl)}>
                        {collection}
                      </Link>
                    </Value>
                  </Item>
                )}
                {sku && (
                  <Item>
                    <Key>{translate(t => t.productPage.articleNumber)}</Key>
                    <Value>{displaySku ?? sku}</Value>
                  </Item>
                )}
              </>
            )}
            {Object.entries(group.productInfo).map(([key, value]) => (
              <Item key={key}>
                <Key>{key}</Key>
                <Value>{value && <Document elements={value} styleDecorators={styles} />}</Value>
              </Item>
            ))}
          </List>
        </ListWrapper>
      ))}
    </>
  );
});

const ListWrapper = styled.div({
  breakInside: 'avoid-column',
  marginBottom: t => t.sharedSpacing.m,
  [':not(:has(h3))' as any]: {
    border: {
      top: {
        color: secondaryGrey,
        width: 1,
        style: 'solid',
      },
    },
  },
});

const List = styled.ul(
  bareListDecorator,
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.xs,
  }),
  {
    width: '100%',
  },
);

const Item = styled.li({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  padding: { y: 6 },
  maxWidth: '100%',
  fontSize: t => t.sharedFontSize.xs,
  border: {
    bottom: {
      color: secondaryGrey,
      width: 1,
      style: 'solid',
    },
  },
  ':last-child': {
    border: {
      bottom: {
        width: 0,
      },
    },
  },
});

const Key = styled.span({
  fontWeight: 300,
});

const Value = styled.span({
  whiteSpace: 'pre-wrap',
  fontWeight: 450,
});

const Link = styled(BaseLink, {
  display: 'block',
  textDecoration: 'underline',
  ':hover': {
    textDecoration: 'underline',
  },
});

const Specifications = styled.div(
  createMinMediumViewportDecorator({
    columns: 2,
    columnGap: t => t.sharedSpacing.xl,
  }),
);

const PartsWrapper = styled.div({
  breakInside: 'avoid-column',
  marginBottom: t => t.sharedSpacing.m,
  [':not(:has(h3))' as any]: {
    border: {
      top: {
        color: secondaryGrey,
        width: 1,
        style: 'solid',
      },
    },
  },
});

const Parts = styled.ul(bareListDecorator, {
  marginBottom: t => t.sharedSpacing.xxl,
});

const PartItem = styled.li({
  display: 'flex',
  marginBottom: t => t.sharedSpacing.xs,
  alignItems: 'baseline',
  fontSize: t => t.sharedFontSize.xs,
});

const PackagePartFactSection = styled.div({
  marginTop: t => t.sharedSpacing.xxl,
  breakInside: 'avoid-column',
});

const Quantity = styled.span({
  fontWeight: 450,
  marginRight: '1ch',
  fontSize: t => t.sharedFontSize.s,
  whiteSpace: 'nowrap',
});

const Sku = styled.span({
  marginLeft: '1ch',
  fontSize: t => t.sharedFontSize.xs,
  whiteSpace: 'nowrap',
});

const ProductDescription = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  maxWidth: 470,
  margin: { x: 'auto' },
});

const PriceHistoryContent = styled.div({
  fontSize: t => t.sharedFontSize.xs,
});

const SubTitle = styled.h3({
  letterSpacing: 0.085,
  fontSize: t => t.sharedFontSize.s,
  marginBottom: t => t.sharedSpacing.m,
});

const StyledSubTitle = styled(SubTitle);

const SubSubTitle = styled.h4({
  letterSpacing: 0.085,
  marginBottom: t => t.sharedSpacing.m,
});

const styles: StyleDecorators = {
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    margin: {
      bottom: 0,
    },
  }),
};

const StyledProductList = styled(ProductListCarousel, {
  marginBottom: t => t.sharedSpacing.xl,
});
